import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Configure axios to send cookies with requests
axios.defaults.withCredentials = true;

// Debug interceptor to log auth issues
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || 'unknown';
      // Only log, don't auto-logout except for /auth/me
      console.warn(`[AUTH_DEBUG] 401 on ${url}`, {
        withCredentials: error.config?.withCredentials,
        cookies: document.cookie ? 'present' : 'empty'
      });
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verify auth status on mount and after actions
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/verify`);
      if (response.data.authenticated) {
        setUser({ username: response.data.username });
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
      setUser(null);
      return false;
    }
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuthStatus().finally(() => setLoading(false));
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      if (response.data.success) {
        // Check if MFA is required
        if (response.data.mfa_required) {
          return { 
            success: true, 
            mfaRequired: true, 
            mfaToken: response.data.mfa_token 
          };
        }
        
        // No MFA - fully authenticated
        setUser({ username: response.data.username });
        return { success: true, mfaRequired: false };
      } else {
        return { success: false, error: 'Login failed' };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const verifyMfa = async (mfaToken, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-mfa`, {
        mfa_token: mfaToken,
        code: code
      });
      
      if (response.data.success) {
        setUser({ username: response.data.username });
        return { success: true };
      } else {
        return { success: false, error: 'Invalid code' };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'MFA verification failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    verifyMfa,
    logout,
    changePassword,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
